import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { getCurrencyName, getCurrencySymbol } from '../../util/fx';
import { useCurrency } from '../../context/currencyContext';

import {
  IconArrowHead,
  IconCheckmark,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
} from '../../components';

import css from './CurrencySwitcher.module.css';

/**
 * Lets the user pick the currency that prices are shown in.
 *
 * This only changes what is displayed: listings are always transacted in the
 * marketplace currency. Converted prices are estimates based on ECB rates.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {('desktop'|'mobile')} [props.layout] - Which topbar the switcher is rendered in
 * @returns {JSX.Element|null}
 */
const CurrencySwitcher = props => {
  const { className, rootClassName, layout = 'desktop' } = props;
  const intl = useIntl();
  const currencyContext = useCurrency();
  const [isOpen, setIsOpen] = useState(false);

  // Rendered outside of a CurrencyProvider (e.g. in tests) or before the config is known.
  if (!currencyContext?.displayCurrency) {
    return null;
  }

  const {
    displayCurrency,
    setDisplayCurrency,
    supportedCurrencies,
    marketplaceCurrency,
  } = currencyContext;
  const classes = classNames(rootClassName || css.root, className);
  // The topbar is tight on mobile, and a narrow symbol alone would be ambiguous
  // between the dollar currencies, so only the code is shown there.
  const isMobileLayout = layout === 'mobile';

  const handleSelect = currency => {
    setDisplayCurrency(currency);
    setIsOpen(false);
  };

  return (
    <Menu
      className={classes}
      isOpen={isOpen}
      onToggleActive={setIsOpen}
      contentPlacementOffset={layout === 'mobile' ? 0 : -8}
    >
      <MenuLabel
        id="currency-switcher-label"
        className={classNames(css.menuLabel, { [css.menuLabelMobile]: isMobileLayout })}
        isOpenClassName={css.menuLabelIsOpen}
        ariaLabel={intl.formatMessage(
          { id: 'CurrencySwitcher.screenreader.label' },
          { currency: displayCurrency }
        )}
      >
        {isMobileLayout ? null : (
          <span className={css.labelSymbol}>{getCurrencySymbol(intl, displayCurrency)}</span>
        )}
        <span className={css.labelCode}>{displayCurrency}</span>
        <IconArrowHead
          className={classNames(css.labelArrow, { [css.labelArrowOpen]: isOpen })}
          direction="down"
          size="small"
        />
      </MenuLabel>
      <MenuContent className={css.menuContent} contentClassName={css.menuContentList}>
        {supportedCurrencies.map(currency => {
          const isSelected = currency === displayCurrency;
          return (
            <MenuItem key={currency}>
              <button
                type="button"
                className={classNames(css.currencyButton, {
                  [css.currencyButtonSelected]: isSelected,
                })}
                onClick={() => handleSelect(currency)}
                aria-current={isSelected ? 'true' : undefined}
              >
                <span className={css.currencySymbol}>{getCurrencySymbol(intl, currency)}</span>
                <span className={css.currencyText}>
                  <span className={css.currencyCode}>{currency}</span>
                  <span className={css.currencyName}>{getCurrencyName(intl, currency)}</span>
                </span>
                {isSelected ? (
                  <IconCheckmark className={css.checkmark} size="small" />
                ) : (
                  <span className={css.checkmarkPlaceholder} />
                )}
              </button>
            </MenuItem>
          );
        })}
      </MenuContent>
    </Menu>
  );
};

export default CurrencySwitcher;
